import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listAdminIncidents, resolveIncident, setUserSuspended, issueStrikeForIncident, assignIncident, setIncidentSeverity, listRepeatOffenders, listAdmins } from "@/lib/incidents.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { ShiftTimeline } from "@/components/ShiftTimeline";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

export const Route = createFileRoute("/_authenticated/admin/incidents")({
  head: () => ({ meta: [{ title: "Incident Reports — Admin" }] }),
  component: AdminIncidents,
});

type Report = {
  id: string;
  shift_id: string;
  booking_id: string | null;
  reporter_id: string;
  reporter_role: string;
  reported_id: string | null;
  type: string;
  description: string;
  evidence_urls: string[];
  worked_minutes: number | null;
  status: string;
  resolution: string | null;
  resolution_pence: number | null;
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
  severity?: "low" | "moderate" | "serious" | "critical";
  assigned_admin_id?: string | null;
  due_at?: string | null;
};
type Shift = { id: string; venue_id: string; start_time: string; end_time: string; hourly_rate: number; status: string; role_type: string };
type Profile = { id: string; full_name: string | null; is_suspended: boolean | null };
type AuditEntry = {
  id: string; shift_id: string; payout_id: string | null; incident_id: string | null;
  action: string; reason: string;
  prior_payout_status: string | null; new_payout_status: string | null;
  prior_shift_status: string | null; new_shift_status: string | null;
  amount_pence: number | null; actor_user_id: string | null; actor_role: string;
  created_at: string;
};
type NotifEntry = {
  id: string; user_id: string; type: string; title: string;
  read_at: string | null; created_at: string;
  metadata: { incident_id?: string; event?: string; party?: string } | null;
};

function AdminIncidents() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(listAdminIncidents);
  const resolve = useServerFn(resolveIncident);
  const suspend = useServerFn(setUserSuspended);
  const issueStrike = useServerFn(issueStrikeForIncident);
  const assignFn = useServerFn(assignIncident);
  const setSeverityFn = useServerFn(setIncidentSeverity);
  const loadRepeat = useServerFn(listRepeatOffenders);
  const loadAdmins = useServerFn(listAdmins);

  const [reports, setReports] = useState<Report[]>([]);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [signed, setSigned] = useState<Record<string, Array<{ path: string; url: string }>>>({});
  const [auditByShift, setAuditByShift] = useState<Record<string, AuditEntry[]>>({});
  const [notifsByIncident, setNotifsByIncident] = useState<Record<string, NotifEntry[]>>({});
  const [filter, setFilter] = useState<"pending" | "all">("pending");
  const [partial, setPartial] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [acting, setActing] = useState<string | null>(null);
  const [admins, setAdmins] = useState<Array<{ id: string; full_name: string | null }>>([]);
  const [repeat, setRepeat] = useState<Array<{ user_id: string; upheld_count: number; serious_count: number; last_incident_at: string; full_name: string | null; role: string; is_suspended: boolean | null }>>([]);

  const refresh = async () => {
    const [r, ro, ad] = await Promise.all([load(), loadRepeat(), loadAdmins()]);
    setReports(r.reports as Report[]);
    setShifts(r.shifts as Shift[]);
    setProfiles(r.profiles as Profile[]);
    setSigned(r.signedEvidence as typeof signed);
    setAuditByShift((r.auditByShift ?? {}) as Record<string, AuditEntry[]>);
    setNotifsByIncident((r.notificationsByIncident ?? {}) as Record<string, NotifEntry[]>);
    setRepeat(ro.rows);
    setAdmins(ad.admins);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await refresh();
    })();
  }, []);

  const onAssign = async (incidentId: string, adminId: string | null) => {
    try { await assignFn({ data: { incident_id: incidentId, admin_id: adminId } }); await refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Assign failed"); }
  };
  const onSetSeverity = async (incidentId: string, severity: "low" | "moderate" | "serious" | "critical") => {
    try { await setSeverityFn({ data: { incident_id: incidentId, severity } }); await refresh(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Severity failed"); }
  };

  const nameOf = (id: string | null) => (id && profiles.find((p) => p.id === id)?.full_name) || "—";
  const shiftOf = (id: string) => shifts.find((s) => s.id === id);

  const visible = reports.filter((r) => filter === "all" || r.status === "pending");

  const act = async (r: Report, kind: "release_full" | "partial" | "refund_venue") => {
    setActing(r.id);
    try {
      const pence = kind === "partial" ? Math.round(parseFloat(partial[r.id] || "0") * 100) : undefined;
      if (kind === "partial" && (!pence || pence <= 0)) { toast.error("Enter a £ amount"); return; }
      await resolve({ data: { incident_id: r.id, resolution: kind, amount_pence: pence, note: notes[r.id] } });
      toast.success("Resolved.");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
    finally { setActing(null); }
  };

  const toggleSuspend = async (userId: string, current: boolean) => {
    try {
      await suspend({ data: { user_id: userId, suspended: !current } });
      toast.success(!current ? "User suspended." : "Suspension lifted.");
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
  };

  const onIssueStrike = async (incidentId: string) => {
    const reason = window.prompt("Reason for the strike (visible to the user):");
    if (!reason || reason.trim().length < 5) return;
    try {
      const res = await issueStrike({ data: { incident_id: incidentId, reason: reason.trim() } });
      toast.success(`Strike ${res.strike_number}/3 issued.`);
      await refresh();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Failed."); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Trust &amp; Safety</p>
            <h1 className="mt-3 font-display text-3xl">Incident Reports</h1>
            <p className="mt-2 text-sm text-muted-foreground">Flagged shifts &amp; early terminations. Payouts are held until you resolve.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Back</Link>
          </div>
        </div>
        <div className="gold-divider mt-4" />

        <div className="mt-6 flex gap-2 text-xs tracking-wide-luxury">
          {(["pending", "all"] as const).map((s) => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1 border ${filter === s ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-accent"}`}>
              {s}
            </button>
          ))}
        </div>

        {repeat.length > 0 && (
          <section className="mt-6 border border-destructive/40 bg-destructive/5 p-4">
            <p className="text-[0.65rem] tracking-wide-luxury text-destructive">REPEAT OFFENDERS · 90 DAYS</p>
            <ul className="mt-2 text-xs space-y-1">
              {repeat.map((u) => (
                <li key={u.user_id} className="flex flex-wrap items-center gap-2">
                  <span className="font-display">{u.full_name ?? u.user_id.slice(0, 8)}</span>
                  <span className="text-muted-foreground">{u.role}</span>
                  <span className="px-1.5 py-0.5 border border-destructive text-destructive text-[0.6rem] tracking-wide-luxury uppercase">{u.upheld_count} upheld</span>
                  {u.serious_count > 0 && (
                    <span className="px-1.5 py-0.5 border border-accent text-accent text-[0.6rem] tracking-wide-luxury uppercase">{u.serious_count} serious</span>
                  )}
                  {u.is_suspended && <span className="px-1.5 py-0.5 border border-muted-foreground text-[0.6rem] tracking-wide-luxury uppercase text-muted-foreground">suspended</span>}
                  <span className="text-[0.65rem] text-muted-foreground">last: {new Date(u.last_incident_at).toLocaleDateString("en-GB")}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-6 space-y-4">
          {visible.length === 0 && (
            <p className="text-sm text-muted-foreground border border-border p-6 text-center">No reports in this view.</p>
          )}
          {visible.map((r) => {
            const s = shiftOf(r.shift_id);
            const reporter = profiles.find((p) => p.id === r.reporter_id);
            const reported = r.reported_id ? profiles.find((p) => p.id === r.reported_id) : null;
            const workedH = ((r.worked_minutes ?? 0) / 60).toFixed(2);
            const fullPence = s ? Math.round(Number(s.hourly_rate) * ((new Date(s.end_time).getTime() - new Date(s.start_time).getTime())/3600000) * 100) : 0;
            const proRataPence = s ? Math.round(Number(s.hourly_rate) * ((r.worked_minutes ?? 0)/60) * 100) : 0;
            return (
              <article key={r.id} className="border border-border bg-card p-5">
                <header className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-2 py-0.5 border border-destructive text-destructive text-[0.6rem] tracking-wide-luxury uppercase">{r.type.replace("_", " ")}</span>
                      <span className={`px-2 py-0.5 border text-[0.6rem] tracking-wide-luxury uppercase ${r.status === "pending" ? "border-accent text-accent" : "border-emerald-700 text-emerald-700"}`}>{r.status}</span>
                      {r.severity && (
                        <span className={`px-2 py-0.5 border text-[0.6rem] tracking-wide-luxury uppercase ${
                          r.severity === "critical" ? "border-destructive text-destructive bg-destructive/10"
                            : r.severity === "serious" ? "border-destructive text-destructive"
                            : r.severity === "moderate" ? "border-accent text-accent"
                            : "border-muted-foreground text-muted-foreground"
                        }`}>{r.severity}</span>
                      )}
                      {r.status === "pending" && r.due_at && new Date(r.due_at).getTime() < Date.now() && (
                        <span className="px-2 py-0.5 border border-destructive text-destructive bg-destructive/10 text-[0.6rem] tracking-wide-luxury uppercase">OVERDUE</span>
                      )}
                      <span className="text-[0.65rem] text-muted-foreground">{new Date(r.created_at).toLocaleString("en-GB")}</span>
                      {r.due_at && r.status === "pending" && (
                        <span className="text-[0.65rem] text-muted-foreground">SLA: {new Date(r.due_at).toLocaleString("en-GB")}</span>
                      )}
                    </div>
                    <h2 className="mt-2 font-display text-lg">
                      {s ? `${s.role_type} · ${new Date(s.start_time).toLocaleString("en-GB")}` : "Shift"}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">
                      Reported by {r.reporter_role}: <b>{nameOf(r.reporter_id)}</b>
                      {reported && <> · against <b>{nameOf(r.reported_id)}</b></>}
                    </p>
                  </div>
                  {reported && (
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">Suspend {nameOf(reported.id).split(" ")[0]}</span>
                      <Switch checked={!!reported.is_suspended} onCheckedChange={() => toggleSuspend(reported.id, !!reported.is_suspended)} />
                    </div>
                  )}
                </header>

                {r.status === "pending" && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-[0.7rem]">
                    <label className="text-muted-foreground tracking-wide-luxury">SEVERITY</label>
                    <select
                      value={r.severity ?? "moderate"}
                      onChange={(e) => onSetSeverity(r.id, e.target.value as "low" | "moderate" | "serious" | "critical")}
                      className="border border-border bg-background px-2 py-1 text-xs"
                    >
                      <option value="low">low</option>
                      <option value="moderate">moderate</option>
                      <option value="serious">serious</option>
                      <option value="critical">critical</option>
                    </select>
                    <label className="text-muted-foreground tracking-wide-luxury ml-2">ASSIGNEE</label>
                    <select
                      value={r.assigned_admin_id ?? ""}
                      onChange={(e) => onAssign(r.id, e.target.value || null)}
                      className="border border-border bg-background px-2 py-1 text-xs"
                    >
                      <option value="">— unassigned —</option>
                      {admins.map((a) => (
                        <option key={a.id} value={a.id}>{a.full_name ?? a.id.slice(0, 8)}</option>
                      ))}
                    </select>
                  </div>
                )}

                <p className="mt-4 text-sm whitespace-pre-wrap">{r.description}</p>


                {r.evidence_urls.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground mb-2">
                      EVIDENCE · admin-only signed links (10 min)
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(signed[r.id] ?? []).map((e) => {
                        const name = e.path.split("/").pop() ?? "file";
                        const isPdf = /\.pdf$/i.test(name);
                        return (
                          <a key={e.path} href={e.url} target="_blank" rel="noreferrer" download={name}
                            className="group block w-28 border border-border overflow-hidden hover:border-accent">
                            {isPdf ? (
                              <div className="h-24 w-full flex items-center justify-center bg-muted/30 text-[0.65rem] tracking-wide-luxury text-muted-foreground">PDF</div>
                            ) : (
                              <img src={e.url} alt={name} className="w-full h-24 object-cover" onError={(ev) => { (ev.target as HTMLImageElement).style.display='none'; }} />
                            )}
                            <p className="px-1.5 py-1 text-[0.6rem] truncate text-muted-foreground group-hover:text-accent">{name}</p>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-5 border-t border-border pt-4">
                  <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground mb-3">SHIFT TIMELINE</p>
                  <ShiftTimeline shiftId={r.shift_id} />
                </div>

                <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div><dt className="text-muted-foreground">Worked</dt><dd className="font-display">{workedH}h</dd></div>
                  <div><dt className="text-muted-foreground">Full payout</dt><dd className="font-display">£{(fullPence/100).toFixed(2)}</dd></div>
                  <div><dt className="text-muted-foreground">Pro-rata</dt><dd className="font-display">£{(proRataPence/100).toFixed(2)}</dd></div>
                  <div><dt className="text-muted-foreground">Shift status</dt><dd className="font-display">{s?.status ?? "—"}</dd></div>
                </dl>

                {r.status === "pending" && (
                  <div className="mt-5 space-y-3 border-t border-border pt-4">
                    <Textarea placeholder="Resolution note (optional)" value={notes[r.id] ?? ""}
                      onChange={(e) => setNotes((p) => ({ ...p, [r.id]: e.target.value }))} rows={2} />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button onClick={() => act(r, "release_full")} disabled={acting === r.id}
                        className="rounded-none bg-emerald-700 hover:bg-emerald-700/90 text-white tracking-wide-luxury">
                        Release Full Payment
                      </Button>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">£</span>
                        <Input type="number" step="0.01" min="0" placeholder={(proRataPence/100).toFixed(2)}
                          value={partial[r.id] ?? ""} onChange={(e) => setPartial((p) => ({ ...p, [r.id]: e.target.value }))}
                          className="w-24 rounded-none h-9 text-sm" />
                        <Button onClick={() => act(r, "partial")} disabled={acting === r.id} variant="outline"
                          className="rounded-none border-accent text-accent tracking-wide-luxury">
                          Issue Partial
                        </Button>
                      </div>
                      <Button onClick={() => act(r, "refund_venue")} disabled={acting === r.id} variant="outline"
                        className="rounded-none border-destructive text-destructive tracking-wide-luxury">
                        Cancel &amp; Refund Venue
                      </Button>
                    </div>
                  </div>
                )}

                {r.status === "resolved" && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Resolved: <b>{r.resolution}</b>
                    {r.resolution_pence != null && <> · £{(r.resolution_pence/100).toFixed(2)}</>}
                    {r.resolution_note && <> · "{r.resolution_note}"</>}
                  </p>
                )}

                {r.reported_id && (
                  <div className="mt-3">
                    <Button onClick={() => onIssueStrike(r.id)} variant="outline"
                      className="rounded-none border-destructive text-destructive tracking-wide-luxury text-xs">
                      Issue strike against {nameOf(r.reported_id).split(" ")[0]}
                    </Button>
                  </div>
                )}

                {(notifsByIncident[r.id]?.length ?? 0) > 0 && (
                  <div className="mt-4 border-t border-border pt-3">
                    <p className="text-[0.65rem] tracking-wide-luxury text-muted-foreground mb-2">IN-APP NOTIFICATION DELIVERY</p>
                    <ul className="space-y-1.5 text-xs">
                      {notifsByIncident[r.id]
                        .slice()
                        .sort((a, b) => a.created_at.localeCompare(b.created_at))
                        .map((n) => {
                          const who = nameOf(n.user_id);
                          const party = n.metadata?.party ?? (n.user_id === r.reporter_id ? "reporter" : "reported");
                          const event = n.metadata?.event ?? "—";
                          const read = !!n.read_at;
                          return (
                            <li key={n.id} className="flex flex-wrap items-center gap-2">
                              <span className="px-1.5 py-0.5 border border-border text-[0.6rem] tracking-wide-luxury uppercase">{event}</span>
                              <span className="text-[0.65rem] text-muted-foreground uppercase">{party}</span>
                              <span>{who}</span>
                              <span className={`px-1.5 py-0.5 text-[0.6rem] tracking-wide-luxury uppercase border ${read ? "border-emerald-700 text-emerald-700" : "border-accent text-accent"}`}>
                                {read ? "read" : "unread"}
                              </span>
                              <span className="text-[0.65rem] text-muted-foreground">
                                sent {new Date(n.created_at).toLocaleString("en-GB")}
                                {read && <> · read {new Date(n.read_at!).toLocaleString("en-GB")}</>}
                              </span>
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}

                {(auditByShift[r.shift_id]?.length ?? 0) > 0 && (
                  <details className="mt-4 border-t border-border pt-3">
                    <summary className="text-[0.65rem] tracking-wide-luxury text-muted-foreground cursor-pointer hover:text-accent">
                      PAYOUT AUDIT LOG ({auditByShift[r.shift_id].length})
                    </summary>
                    <ul className="mt-3 space-y-2 text-xs">
                      {auditByShift[r.shift_id].map((a) => (
                        <li key={a.id} className="border-l-2 border-border pl-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="px-1.5 py-0.5 border border-border text-[0.6rem] tracking-wide-luxury uppercase">{a.action.replace(/_/g, " ")}</span>
                            <span className="text-[0.65rem] text-muted-foreground uppercase">{a.actor_role}</span>
                            <span className="text-[0.65rem] text-muted-foreground">{new Date(a.created_at).toLocaleString("en-GB")}</span>
                            {a.amount_pence != null && (
                              <span className="text-[0.65rem]">£{(a.amount_pence/100).toFixed(2)}</span>
                            )}
                          </div>
                          <p className="mt-1 text-muted-foreground">{a.reason}</p>
                          {(a.prior_payout_status || a.new_payout_status) && (
                            <p className="mt-0.5 text-[0.65rem] text-muted-foreground">
                              payout: {a.prior_payout_status ?? "—"} → {a.new_payout_status ?? "—"}
                            </p>
                          )}
                          {(a.prior_shift_status !== a.new_shift_status) && (a.prior_shift_status || a.new_shift_status) && (
                            <p className="mt-0.5 text-[0.65rem] text-muted-foreground">
                              shift: {a.prior_shift_status ?? "—"} → {a.new_shift_status ?? "—"}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </article>
            );
          })}
        </div>
      </main>
    </div>
  );
}
